import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Save, Send, Loader2, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { COUNTRIES } from '../utils/locationData';
import SEO from '../components/SEO';
import { seoConfig } from '../config/seoConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PostJobPage = () => {
  const { jobId } = useParams(); // If editing existing job
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jobsRemaining, setJobsRemaining] = useState(3);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    company_name: '',
    job_type: 'full_time',
    work_location: 'remote',
    country: 'United States',
    state: '',
    city: '',
    description: '',
    requirements: '',
    salary_min: '',
    salary_max: '',
    salary_currency: 'USD',
    experience_level: 'mid',
    skills: '',
    application_method: 'platform',
    application_url: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchJobsRemaining();
    
    if (jobId) {
      fetchJobData();
    }
  }, [isAuthenticated, jobId]);

  const fetchJobsRemaining = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/my-jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobsRemaining(data.jobs_remaining || 0);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchJobData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const job = data.job;
        
        setFormData({
          title: job.title || '',
          company_name: job.company_name || '',
          job_type: job.job_type || 'full_time',
          work_location: job.work_location || 'remote',
          country: job.country || 'United States',
          state: job.state || '',
          city: job.city || '',
          description: job.description || '',
          requirements: job.requirements || '',
          salary_min: job.salary_min || '',
          salary_max: job.salary_max || '',
          salary_currency: job.salary_currency || 'USD',
          experience_level: job.experience_level || 'mid',
          skills: job.skills ? job.skills.join(', ') : '',
          application_method: job.application_method || 'platform',
          application_url: job.application_url || ''
        });
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      toast({ title: 'Error', description: 'Failed to load job', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e, action = 'draft') => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim() || !formData.company_name.trim() || !formData.description.trim()) {
      toast({ 
        title: 'Required Fields', 
        description: 'Please fill in title, company name, and description', 
        variant: 'destructive' 
      });
      return;
    }

    if (formData.application_method === 'external_url' && !formData.application_url.trim()) {
      toast({ 
        title: 'Application URL Required', 
        description: 'Please provide the application URL', 
        variant: 'destructive' 
      });
      return;
    }

    setSaving(true);
    try {
      // Prepare data
      const jobData = {
        ...formData,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(s => s) : []
      };

      // Create or update
      const url = jobId 
        ? `${BACKEND_URL}/api/jobs/${jobId}`
        : `${BACKEND_URL}/api/jobs/create`;
      
      const method = jobId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jobData)
      });

      if (response.ok) {
        const data = await response.json();
        const createdJobId = data.job_id || jobId;
        
        // If publishing, call publish endpoint
        if (action === 'publish') {
          const publishResponse = await fetch(
            `${BACKEND_URL}/api/jobs/${createdJobId}/publish`,
            {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          
          if (publishResponse.ok) {
            toast({ 
              title: 'Success!', 
              description: 'Job published successfully',
              duration: 3000
            });
          }
        } else {
          toast({ 
            title: 'Success!', 
            description: jobId ? 'Job updated successfully' : 'Job saved as draft',
            duration: 3000
          });
        }
        
        navigate('/my-jobs');
      } else {
        const error = await response.json();
        toast({ 
          title: 'Error', 
          description: error.detail || 'Failed to save job', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Error saving job:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to save job. Please try again.', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <>
        <SEO 
          title={seoConfig.postJob.title}
          description={seoConfig.postJob.description}
          keywords={seoConfig.postJob.keywords}
          canonical={seoConfig.postJob.canonical}
        />
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title={seoConfig.postJob.title}
        description={seoConfig.postJob.description}
        keywords={seoConfig.postJob.keywords}
        canonical={seoConfig.postJob.canonical}
      />
      <div className="min-h-screen bg-slate-50 py-8 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex gap-3 mb-4">
            <Button 
              onClick={() => navigate('/')} 
              variant="outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <Button 
              onClick={() => navigate('/my-jobs')} 
              variant="outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to My Jobs
            </Button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                {jobId ? 'Edit Job' : 'Post a New Job'}
              </h1>
              <p className="text-slate-600 mt-1">
                Fill in the details below to post your job
              </p>
            </div>
            {!jobId && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-300 px-4 py-2">
                {jobsRemaining} / 3 jobs remaining
              </Badge>
            )}
          </div>
        </div>

        {/* Warning if no jobs remaining */}
        {!jobId && jobsRemaining === 0 && (
          <Card className="mb-6 border-2 border-orange-500 bg-orange-50">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-orange-900">Maximum Jobs Reached</p>
                  <p className="text-sm text-orange-700 mt-1">
                    You have reached the limit of 3 active jobs. Please delete or pause an existing job to post a new one.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Job Details</CardTitle>
            <CardDescription>Enter the job information</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => handleSubmit(e, 'draft')} className="space-y-6">
              {/* Job Title */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Job Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Senior Talent Acquisition Specialist"
                  className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  required
                />
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="e.g., Tech Corp Inc."
                  className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  required
                />
              </div>

              {/* Job Type and Work Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Job Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="job_type"
                    value={formData.job_type}
                    onChange={handleChange}
                    className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  >
                    <option value="full_time">Full-time</option>
                    <option value="part_time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                    <option value="temporary">Temporary</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Work Location <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="work_location"
                    value={formData.work_location}
                    onChange={handleChange}
                    className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  >
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="onsite">Onsite</option>
                  </select>
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  >
                    {COUNTRIES.map(country => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    State/Province
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="e.g., California"
                    className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="e.g., San Francisco"
                    className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                </div>
              </div>

              {/* Job Description */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Job Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the job role, responsibilities, and what makes this opportunity great..."
                  className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none min-h-[150px]"
                  required
                />
              </div>

              {/* Requirements */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Requirements
                </label>
                <textarea
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  placeholder="List the qualifications, skills, and experience required..."
                  className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none min-h-[120px]"
                />
              </div>

              {/* Salary Range */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Salary Range (Optional)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="number"
                    name="salary_min"
                    value={formData.salary_min}
                    onChange={handleChange}
                    placeholder="Min (e.g., 80000)"
                    className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                  <input
                    type="number"
                    name="salary_max"
                    value={formData.salary_max}
                    onChange={handleChange}
                    placeholder="Max (e.g., 120000)"
                    className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  />
                  <select
                    name="salary_currency"
                    value={formData.salary_currency}
                    onChange={handleChange}
                    className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="INR">INR (₹)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="CAD">CAD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>
              </div>

              {/* Experience Level */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Experience Level
                </label>
                <select
                  name="experience_level"
                  value={formData.experience_level}
                  onChange={handleChange}
                  className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                >
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                  <option value="executive">Executive</option>
                </select>
              </div>

              {/* Skills */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Skills/Tags
                </label>
                <input
                  type="text"
                  name="skills"
                  value={formData.skills}
                  onChange={handleChange}
                  placeholder="e.g., Recruiting, ATS, Boolean Search, LinkedIn Recruiter"
                  className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none"
                />
                <p className="text-sm text-slate-500 mt-1">Separate skills with commas</p>
              </div>

              {/* Application Method */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  How should candidates apply?
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border-2 border-slate-300 rounded-lg cursor-pointer hover:border-blue-500">
                    <input
                      type="radio"
                      name="application_method"
                      value="platform"
                      checked={formData.application_method === 'platform'}
                      onChange={handleChange}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">Apply on RecruiterIQ</p>
                      <p className="text-sm text-slate-600">Candidates apply directly on our platform</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border-2 border-slate-300 rounded-lg cursor-pointer hover:border-blue-500">
                    <input
                      type="radio"
                      name="application_method"
                      value="external_url"
                      checked={formData.application_method === 'external_url'}
                      onChange={handleChange}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">External Application URL</p>
                      <p className="text-sm text-slate-600">Redirect to your company's career page</p>
                    </div>
                  </label>
                </div>

                {formData.application_method === 'external_url' && (
                  <input
                    type="url"
                    name="application_url"
                    value={formData.application_url}
                    onChange={handleChange}
                    placeholder="https://yourcompany.com/careers/apply"
                    className="w-full p-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:outline-none mt-3"
                    required
                  />
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  type="submit"
                  variant="outline"
                  disabled={saving || (!jobId && jobsRemaining === 0)}
                  className="flex-1"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save as Draft
                </Button>

                <Button
                  type="button"
                  onClick={(e) => handleSubmit(e, 'publish')}
                  disabled={saving || (!jobId && jobsRemaining === 0)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Publish Job
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      </div>
    </>
  );
};

export default PostJobPage;
